import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { registerChunkLoadErrorHandler } from '@jjj/move';
import { CONSENT_CATEGORIES, initCookieConsent, runWhenCategoryAllowed } from '@jjj/components';
import './polyfills';
import { store } from './redux/store';
import DashApp from './dashApp';
import { unregister } from './registerServiceWorker';
import './index.css';
import appSettings, { isOneTrustEnabled, isProduction, isStaging } from './settings';
import build from './helpers/buildHelpers';
import './i18n';
import { loadGoogleMapsScript } from './helpers/loadGoogleMaps';
import { getTimeFormat, is12HoursTimeFormat } from './helpers/dateTime';

// Must run before anything gates on consent — the shared helper cannot read this app's build config.
initCookieConsent({ enabled: isOneTrustEnabled });

// Expose dateTime helpers globally for webcore-components
if (typeof window !== 'undefined') {
  (window as any).getTimeFormat = getTimeFormat;
  (window as any).is12HoursTimeFormat = is12HoursTimeFormat;
}

loadGoogleMapsScript();

registerChunkLoadErrorHandler();

unregister();

const { dataDogApplicationId: applicationId, dataDogClientToken: clientToken, appEnv: environment } = appSettings;

runWhenCategoryAllowed(CONSENT_CATEGORIES.performance, () => {
  if ((isProduction || isStaging) && applicationId && clientToken) {
    datadogRum.init({
      applicationId,
      clientToken,
      service: 'dash-web',
      site: 'datadoghq.com',
      env: environment,
      beforeSend: event => {
        // @ts-ignore
        if (['Non-Error exception captured', 'Non-Error promise rejection captured'].includes(event?.error?.message)) {
          return false;
        }
      },
      // Specify a version number to identify the deployed version of your application in Datadog
      version: build.release,
      sessionSampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
    });
  }
});

const container = document.getElementById('root');

if (container) {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <DashApp />
    </Provider>,
  );
} else {
  // eslint-disable-next-line no-console
  console.error('Root container not found');
}
